import React from 'react'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'

const AlertMessage = ({ activeCoverageEnhanced }) => {
  let items = []
  if (!activeCoverageEnhanced) {
    return null
  }

  if (Array.isArray(activeCoverageEnhanced)) {
    items = activeCoverageEnhanced
  } else if (typeof activeCoverageEnhanced === 'object') {
    items = [activeCoverageEnhanced]
  }

  if (items.length === 0) {
    return null
  }

  const getAlertStyles = alertLevel => {
    const styleMap = {
      error: {
        backgroundColor: '#FEF2F2',
        borderColor: '#DC2626',
        titleColor: '#991B1B',
        textColor: '#7F1D1D',
        title: 'Action Required',
      },
      warning: {
        backgroundColor: '#FFFBEB',
        borderColor: '#F59E0B',
        titleColor: '#92400E',
        textColor: '#78350F',
        title: 'Coverage Notice',
      },
      info: {
        backgroundColor: '#EFF6FF',
        borderColor: '#3B82F6',
        titleColor: '#1E3A8A',
        textColor: '#1E40AF',
        title: 'Coverage Information',
      },
      default: {
        backgroundColor: '#F9FAFB',
        borderColor: '#6B7280',
        titleColor: '#374151',
        textColor: '#4B5563',
        title: 'Message',
      },
    }
    return styleMap[alertLevel] || styleMap.default
  }

  const renderContactInfo = (phones = [], urls = []) => {
    if (phones.length === 0 && urls.length === 0) {
      return null
    }

    return (
      <Box
        mt={1.5}
        pt={1.5}
        sx={{
          borderTop: '1px solid rgba(0,0,0,0.1)',
        }}
      >
        {phones.length > 0 && (
          <Box mb={urls.length > 0 ? 1 : 0}>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ mb: 0.5, fontSize: '0.8125rem' }}
            >
              Contact:
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
              }}
            >
              {phones.map((phone, index) => (
                <Link
                  key={index}
                  href={`tel:${phone}`}
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: '#2563EB',
                    textDecoration: 'none',
                    wordBreak: 'break-all',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {phone}
                </Link>
              ))}
            </Box>
          </Box>
        )}
        {urls.length > 0 && (
          <Box>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ mb: 0.5, fontSize: '0.8125rem' }}
            >
              More Information:
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              {urls.map((url, index) => (
                <Link
                  key={index}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: '#2563EB',
                    textDecoration: 'none',
                    wordBreak: 'break-all',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '&:hover': {
                      textDecoration: 'underline',
                      overflow: 'visible',
                    },
                  }}
                >
                  {url}
                </Link>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box sx={{ width: '92%', maxWidth: '552px' }}>
      {items.map((item, index) => {
        const message = item.message || {}
        const styles = getAlertStyles(message.alertLevel)

        return (
          <Box
            key={index}
            sx={{
              mb: 2,
              p: 1.5,
              backgroundColor: styles.backgroundColor,
              borderLeft: `4px solid ${styles.borderColor}`,
              borderRadius: '4px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              boxSizing: 'border-box',
              width: '100%',
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  color: styles.titleColor,
                  mb: 0.5,
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                {styles.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.8125rem',
                  color: styles.textColor,
                  lineHeight: 1.5,
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                {message.text || 'No message available'}
              </Typography>
              {renderContactInfo(message.phones, message.urls)}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export default AlertMessage
